import React, { useState, useEffect, useRef } from 'react';
import './DailyProblem.css';
import { runCode, submitCode, getLastSubmission } from '../api/judge-api';
import CodeEditor from '../components/DailyProblem/CodeEditor';
import ResizeBar from '../components/DailyProblem/ResizeBar';
import { getDailyProblem } from '../firebase/auth';
import { useAuth } from '../contexts/authContext';
import { useNavigate } from 'react-router-dom';

export default function DailyProblem() {
  // State management
  const [language, setLanguage] = useState('python3');
  const [code, setCode] = useState('');
  const [stdin, setStdin] = useState('');
  const [output, setOutput] = useState('Output will appear here...');
  const [isRunning, setIsRunning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Daily problem state
  const [dailyProblem, setDailyProblem] = useState(null);
  const [isLoadingProblem, setIsLoadingProblem] = useState(true);
  const [problemError, setProblemError] = useState(null);

  // Panel sizing state
  const [leftPanelWidth, setLeftPanelWidth] = useState(500);
  const [codeEditorHeight, setCodeEditorHeight] = useState(400);

  // Refs for resize targets
  const problemDescRef = useRef(null);
  const codeEditorContainerRef = useRef(null);

  const { userLoggedIn, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Calculate constraints based on window size percentages
  const getLeftPanelConstraints = () => {
    const windowWidth = window.innerWidth;
    // console.log("Window width: ", windowWidth);
    return {
      minSize: windowWidth * 0.2, // 20% of window width
      maxSize: windowWidth * 0.7   // 70% of window width
    };
  };

  const getCodeEditorConstraints = () => {
    const windowHeight = window.innerHeight;
    const navbarHeight = 100; // Approximate navbar height
    const margins = 32; // Total margins and padding
    const availableHeight = windowHeight - navbarHeight - margins;
    console.log("max Height: ", availableHeight*0.85);
    
    return {
      minSize: availableHeight * 0.15, // 15% of available height
      maxSize: availableHeight * 0.85   // 85% of available height
    };
  };

  // Fetch daily problem on component mount
  useEffect(() => {
    const fetchDailyProblem = async () => {
      try {
        setIsLoadingProblem(true);
        setProblemError(null);
        const problem = await getDailyProblem();
        setDailyProblem(problem);
      } catch (error) {
        console.error('Error fetching daily problem:', error);
        setProblemError(error.message);
      } finally {
        setIsLoadingProblem(false);
      }
    };

    fetchDailyProblem();
  }, []);

  // Start from the user's last submission for this problem
  const codeRef = useRef(code);
  codeRef.current = code;

  useEffect(() => {
    if (!dailyProblem || !userLoggedIn) return;

    let cancelled = false;
    getLastSubmission(dailyProblem.cpid)
      .then(last => {
        // Leave the editor alone if the user has already typed something.
        if (cancelled || !last || codeRef.current) return;
        setLanguage(last.language);
        setCode(last.code);
      })
      .catch(error => console.error('Error fetching last submission:', error));

    return () => {
      cancelled = true;
    };
  }, [dailyProblem, userLoggedIn]);

  // Handle window resize to update constraints
  useEffect(() => {
    const handleWindowResize = () => {
      // Recalculate constraints when window resizes
      const leftConstraints = getLeftPanelConstraints();
      const codeEditorConstraints = getCodeEditorConstraints();
      
      // Ensure current sizes are within new constraints
      setLeftPanelWidth(prev => Math.max(leftConstraints.minSize, Math.min(leftConstraints.maxSize, prev)));
      setCodeEditorHeight(prev => Math.max(codeEditorConstraints.minSize, Math.min(codeEditorConstraints.maxSize, prev)));
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  const formatSubmissionResult = (verdict) => {
    if (verdict === "AC") {
      return "✅ Correct Answer";
    } else if (verdict === "WA") {
      return "❌ Wrong Answer";
    } else if (verdict === "TLE") {
      return "⏰ Time Limit Exceeded";
    } else if (verdict === "MLE") {
      return "💾 Memory Limit Exceeded";
    } else if (verdict === "CE") {
      return "🔨 Compilation Error";
    }
    return "💥 Runtime Error";
  };

  // Helper function to format the result of a run
  const formatRunResult = (result) => {
    if (result.verdict === "CE") {
      return "🔨 Compilation Error\n\n" + result.compileOutput;
    }

    let resultText = "";
    if (result.verdict === "RE") {
      resultText = "💥 Runtime Error\n\nError:\n" + result.stderr;
    } else if (result.verdict === "TLE") {
      resultText = "⏰ Time Limit Exceeded";
    } else if (result.verdict === "MLE") {
      resultText = "💾 Memory Limit Exceeded";
    } else {
      resultText = "Output:\n" + result.stdout;
    }

    if (result.truncated) {
      resultText += "\n\n(Output was truncated.)";
    }

    // Add execution time and memory info
    const memoryUsed = result.memoryKb ? `${result.memoryKb}KB` : 'N/A';
    resultText += `\n\nExecution Time: ${result.timeMs}ms`;
    resultText += `\nMemory Used: ${memoryUsed}`;

    return resultText;
  };

  // Event handlers
  const handleRunCode = async () => {
    if (!code.trim()) {
      setOutput('Please enter some code to run.');
      return;
    }

    setIsRunning(true);
    setError(null);
    setOutput('Uploading and running...');

    try {
      const result = await runCode(code, stdin, language);
      const resultText = formatRunResult(result);
      setOutput(resultText);
    } catch (error) {
      console.error('Error running code:', error);
      setError(error.message);
      setOutput('⚠️ Server Error: ' + error.message + '\n\nThis is not a problem with your code. Please try again.');
    } finally {
      setIsRunning(false);
    }
  };

  const handleSubmitCode = async () => {
    if (!code.trim()) {
      setOutput('Please enter some code to submit.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setOutput('Submitting...');

    try {
      // Each poll returns every finished case, so only print the new ones.
      let shown = 0;
      const showProgress = (progress) => {
        if (!progress.cases) return;
        if (shown === 0) {
          setOutput(`Running ${progress.total} test cases...\n\n`);
        }
        const lines = progress.cases
          .slice(shown)
          .map(c => `Test Case #${c.testId}: ${formatSubmissionResult(c.verdict)}\n`)
          .join('');
        shown = progress.cases.length;
        if (lines) {
          setOutput(prev => prev + lines);
        }
      };

      const result = await submitCode(code, dailyProblem.cpid, language, showProgress);

      if (result.verdict === 'CE') {
        setOutput(formatSubmissionResult('CE') + '\n\n' + result.compileOutput);
      } else {
        let summary = `\nPassed ${result.passed} out of ${result.total} test cases.\n`;
        if (result.firstFailure?.stderr) {
          summary += `\nError on Test Case #${result.firstFailure.testId}:\n${result.firstFailure.stderr}`;
        }
        setOutput(prev => prev + summary);
      }
    } catch (error) {
      console.error('Error submitting code:', error);
      setError(error.message);
      setOutput('⚠️ Server Error: ' + error.message + '\n\nThis is not a problem with your code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dailyProblem">
      {!authLoading && !userLoggedIn && (
        <div className="overlay-blur">
          <div className="overlay-center-content">
            <h2>You must be logged in to access the Daily Problem page.</h2>
            <button className="btn btn-primary" onClick={() => navigate('/login')}>Login</button>
          </div>
        </div>
      )}
      <div 
        ref={problemDescRef}
        className="problemDesc"
        style={{ width: `${leftPanelWidth}px` }}
      >
        {isLoadingProblem ? (
          <div>
            <h2>Loading today's problem...</h2>
            <p>Please wait while we fetch your daily challenge.</p>
          </div>
        ) : problemError ? (
          <div>
            <h2>Error Loading Problem</h2>
            <p>Sorry, we couldn't load today's problem: {problemError}</p>
            <p>Please try refreshing the page.</p>
          </div>
        ) : dailyProblem ? (
          <div>
            <h2>{dailyProblem.title}</h2>
            <p><strong>Contest:</strong> USACO {dailyProblem.year} {dailyProblem.month} Contest, {dailyProblem.division}</p>
            <p><strong>Problem Number:</strong> {dailyProblem['problem-number']}</p>
            <div style={{ marginTop: '20px' }}>
              <h3>Problem Description</h3>
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                {dailyProblem.description}
              </div>
            </div>
            {dailyProblem.url && (
              <div style={{ marginTop: '20px' }}>
                <p><strong>Original Problem:</strong> <a href={dailyProblem.url} target="_blank" rel="noopener noreferrer">View on USACO Website</a></p>
              </div>
            )}
          </div>
        ) : (
          <div>
            <h2>No Problem Available</h2>
            <p>Sorry, no problem is available at the moment.</p>
          </div>
        )}
      </div>

      <ResizeBar 
        direction="vertical" 
        onResize={setLeftPanelWidth}
        minSize={getLeftPanelConstraints().minSize}
        maxSize={getLeftPanelConstraints().maxSize}
        targetElement={problemDescRef.current}
      />

        <div className="rightColumn">
          <CodeEditor
            ref={codeEditorContainerRef}
            value={code}
            onChange={setCode}
            languageId={language}
            height={`${codeEditorHeight}px`}
          />

          <ResizeBar 
            direction="horizontal" 
            onResize={setCodeEditorHeight}
            minSize={getCodeEditorConstraints().minSize}
            maxSize={getCodeEditorConstraints().maxSize}
            targetElement={codeEditorContainerRef.current}
          />

          <div className="bottomRight">
            <div className="form-group inputContainer">
              <label htmlFor="stdin">Input (stdin):</label>
              <br />
              <textarea
                id="stdin"
                placeholder="Enter input here (optional)..."
                value={stdin}
                onChange={(e) => setStdin(e.target.value)}
              />

              <div className="controls-group">
                <div className="languageSelector">
                  <label htmlFor="language">Language:</label>
                  <select
                    id="language"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                  >
                    <option value="python3">Python 3</option>
                    <option value="cpp">C++</option>
                    <option value="java">Java</option>
                  </select>
                </div>
                
                <div className="button-group">
                  <button
                    className="btn btn-secondary"
                    onClick={handleRunCode}
                    disabled={isRunning || isSubmitting}
                  >
                    {isRunning ? 'Running...' : 'Run Code'}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSubmitCode}
                    disabled={isRunning || isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </div>
            </div>

            <div className="outputContainer">
              <pre>{output}</pre>
              {error && (
                <div className="error-message">
                  <strong>Error:</strong> {error}
                </div>
              )}
            </div>
          </div>
        </div>
    </div>
  );
}